import { createAsyncThunk } from '@reduxjs/toolkit';
import { get } from 'lodash';

import { ApiResListsType, OhsApiRequestName, RequestType } from 'global-services/api/OhsApiModels';
import {
  OhsVisitorRootState,
  OhsVisitorFilterPayload,
  OhsVisitorSiteSearchMain,
} from 'visitor/OhsVisitorModels';
import { OhsUser } from 'user/OhsUser';
import OhsApiRequest from 'global-services/api/OhsApiRequest';
import { ModuleType, TierType } from 'global-services/constants/OhsObject';
import OhsUserLocalServices from 'user/OhsUserLocalServices';

import {
  getVisitorSiteLists,
  getVisitorSiteActivityLists,
  getVisitorSiteSearchLists,
} from '../site/OhsVisitorSiteServices';
import { getVisitorFormLists } from '../form/OhsVisitorFormServices';
import { getVisitorPinLists } from '../pin/OhsVisitorPinServices';
import OhsVisitorSiteRecord from '../site/models/OhsVisitorSiteRecord';
import OhsVisitorFormRecord from '../form/models/OhsVisitorFormRecord';
import OhsVisitorPinRecord from '../pin/OhsVisitorPinRecord';

// VISITOR SITE ACTIVITY SEARCH
const getVisitorSiteActivitySearchLists = (
  searchPayload: any
): Promise<ApiResListsType<any[]> | null> => {
  searchPayload.filter.leave = searchPayload.filter.leave ? false : null;
  return OhsApiRequest(
    RequestType.Module,
    ModuleType.Visitor,
    OhsApiRequestName.VisitSearch,
    searchPayload
  );
};

// SITE - LIST
const visitorSiteListsRPC = async (
  payload: OhsVisitorFilterPayload | OhsVisitorSiteSearchMain,
  hasSearch: boolean = false
): Promise<any> => {
  const user = OhsUserLocalServices.getLocalOhsUser() as OhsUser;
  const validUser = user.tier.type === TierType.T3 || user.tier.type === TierType.T4;

  if (validUser && user?.configs?.visitor?.PERMISSIONS.site_view) {
    const response = hasSearch
      ? await getVisitorSiteSearchLists(payload as OhsVisitorSiteSearchMain)
      : await getVisitorSiteLists(payload as OhsVisitorFilterPayload);
    return response;
  }
  return null;
};

export const fetchVisitorSiteListsAsync = createAsyncThunk<
  {
    list: ApiResListsType<OhsVisitorSiteRecord[]> | null;
    count: ApiResListsType<OhsVisitorSiteRecord[]> | null;
  },
  {
    list: OhsVisitorFilterPayload | OhsVisitorSiteSearchMain;
    count: OhsVisitorFilterPayload | OhsVisitorSiteSearchMain;
    hasSearch: boolean;
  }
>('visitor/site/fetchVisitorSiteListsAsync', async (payload, thunkAPI) => {
  try {
    const [list, count] = await Promise.all([
      payload.hasSearch
        ? visitorSiteListsRPC(payload.list as OhsVisitorSiteSearchMain, true)
        : visitorSiteListsRPC(payload.list as OhsVisitorFilterPayload),
      payload.hasSearch
        ? visitorSiteListsRPC(payload.count as OhsVisitorSiteSearchMain, true)
        : getVisitorSiteLists(payload.count as OhsVisitorFilterPayload),
    ]);

    return { list, count };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});

// SITE - ACTIVITY LIST
export const fetchVisitorSiteActivityListCountAsync = createAsyncThunk<
  ApiResListsType<OhsVisitorSiteRecord[]> | null,
  {
    hasSearch: boolean;
    list: any;
    count: any;
    siteId: string;
  }
>('visitor/site/fetchVisitorSiteActivityListCountAsync', async (payload, thunkAPI) => {
  try {
    const response = payload.hasSearch
      ? getVisitorSiteActivitySearchLists(payload.count)
      : getVisitorSiteActivityLists(payload.siteId, payload.count);
    return await response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});

export const fetchVisitorSiteActivityListsAsync = createAsyncThunk<
  ApiResListsType<OhsVisitorSiteRecord[]> | null,
  {
    hasSearch: boolean;
    list: any;
    count: any;
    siteId: string;
  }
>('visitor/site/fetchVisitorSiteActivityListsAsync', async (payload, thunkAPI) => {
  try {
    const response = payload.hasSearch
      ? getVisitorSiteActivitySearchLists(payload.list)
      : getVisitorSiteActivityLists(payload.siteId, payload.list);
    return await response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});

// FORM
export const fetchVisitorFormListCountAsync = createAsyncThunk<
  ApiResListsType<OhsVisitorFormRecord[]> | null,
  undefined
>('visitor/form/fetchVisitorFormCountList', async (_, thunkAPI) => {
  const {
    visitor,
    globalfilter: { filterInfo },
  } = thunkAPI.getState() as OhsVisitorRootState;

  const { currentViewPreset } = visitor;

  const viewPresetFilter = filterInfo.visitorModule[currentViewPreset];

  const visitorFiltersWithCount: OhsVisitorFilterPayload = {
    ...viewPresetFilter,
    count: true,
    page: 1,
    sort: { order: -1, key: 'dateCreated' },
  };

  try {
    const response = await getVisitorFormLists(visitorFiltersWithCount);
    return response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});

export const fetchVisitorFormListsAsync = createAsyncThunk<
  ApiResListsType<OhsVisitorFormRecord[]> | null,
  undefined
>('visitor/form/fetchVisitorFormList', async (_, thunkAPI) => {
  const {
    visitor,
    globalfilter: { filterInfo },
  } = thunkAPI.getState() as OhsVisitorRootState;
  const data = visitor.visitorFormList;
  const { currentPage, currentViewPreset } = visitor;

  const viewPresetFilter = filterInfo.visitorModule[currentViewPreset];

  const visitorSort = get(filterInfo, `visitorModule.${currentViewPreset}.sort`) || '';
  const setVisitorFilters: OhsVisitorFilterPayload = {
    ...viewPresetFilter,
    next: currentPage > 10 ? data?.pagination?.next ?? '' : '',
    page: visitor.currentPage > 10 ? 0 : visitor.currentPage,
    sort: { ...JSON.parse(String(visitorSort)) },
    count: false,
  };

  try {
    const response = await getVisitorFormLists(setVisitorFilters);
    return response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});

// PIN
export const fetchVisitorPinListsAsync = createAsyncThunk<OhsVisitorPinRecord[] | null, OhsUser>(
  'visitor/pin/fetchVisitorPinList',
  async (user: OhsUser, thunkAPI) => {
    try {
      const response = await getVisitorPinLists(user);
      return response;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);
